import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Typography,
  PLATFORM_THEME_COLORS,
  ICONS,
  Icon,
  Form,
  TextInput,
  Separator,
  DeleteModal,
  SelectInput,
  NumberInput,
} from '@thinknet-gmbh/platform-common-ui';

import { ILocationFormListProps } from 'src/pages/settings/components//company-profile/components/company-common-form/company-common-form.interfaces';
import { useDeleteCompanyLocationById } from 'src/hooks/companies/delete-location-by-id.hook';

import { BusinessAddressFormList } from './components/business-address-form-list';
import { LocationWrapper, DeleteContainer, FormItemWrapper } from './location-form-list.styles';

const LocationFormList = ({
  fields,
  remove,
  add,
  isAddedNewLocation,
  form,
  datevNumbers,
  editCompany,
  companyDatevNumberId,
  canAddDatevNumber,
}: ILocationFormListProps) => {
  const { t } = useTranslation(['common', 'settings']);
  const [companyLocationToDelete, setCompanyLocationToDelete] = useState<any>(null);
  const [selectedDatevNumberId, setSelectedDatevNumberId] = useState('');

  const deleteCompanyLocation = useDeleteCompanyLocationById();

  const deleteLocationHandler = (name: number) => {
    const locationData = form.getFieldValue(['locations', name]);

    if (locationData._id) {
      setCompanyLocationToDelete({ location: locationData, field: name });
    } else {
      remove(name);
    }
  };

  const onSubmitDeleteModal = () => {
    deleteCompanyLocation({
      variables: { locationId: companyLocationToDelete.location._id },
      onCompleted: data => {
        if (data) {
          remove(companyLocationToDelete.field);
          setCompanyLocationToDelete(null);
        }
      },
    });
  };

  useEffect(() => {
    if (isAddedNewLocation) {
      add({
        businessAddresses: [{ departments: [{}] }],
      });
    }
  }, [isAddedNewLocation, add]);

  return (
    <div>
      <DeleteModal
        title={t('settings:company.deleteModal.title')}
        subTitle={t('settings:company.deleteModal.subtitle', {
          locationName: companyLocationToDelete?.location?.name,
        })}
        onSubmit={() => onSubmitDeleteModal()}
        submitBtnText={t('common:buttons.delete')}
        translate={t}
        onClose={() => setCompanyLocationToDelete(null)}
        isOpened={!!companyLocationToDelete}
      />

      {fields.map(field => (
        <LocationWrapper key={field.key}>
          <DeleteContainer>
            <Typography weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
              {`${t('common:labels.location')} ${fields.length > 1 ? field.name + 1 : ''}`}
            </Typography>
            {fields.length > 1 && (
              <Icon
                icon={ICONS.trash}
                color={PLATFORM_THEME_COLORS.tone.red}
                rightMargin={false}
                size={18}
                onClick={() => deleteLocationHandler(field.name)}
              />
            )}
          </DeleteContainer>
          <FormItemWrapper>
            <Form.Item
              name={[field.name, 'name']}
              label={t('settings:holidayManager.labels.locationName')}
              rules={[
                {
                  required: true,
                  message: t('settings:holidayManager.validation.locationNameRequired'),
                },
              ]}
            >
              <TextInput maxLength={100} placeholder={t('settings:company.placeholders.locationName')} />
            </Form.Item>
            {canAddDatevNumber && (
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const datevNumberId = getFieldValue(['locations', field.name, 'datevNumberId']);
                  const locationDatevNumber = editCompany?.locations?.find(
                    (item: any) => item.datevNumber?._id === datevNumberId
                  );

                  return (
                    <Form.Item
                      name={[field.name, 'datevNumberId']}
                      label={t('settings:company.labels.datevNumber')}
                      rules={[
                        {
                          required: canAddDatevNumber,
                          message: t('settings:company.exceptions.requiredDATEVNumber'),
                        },
                      ]}
                    >
                      <SelectInput placeholder="№346347" onSelect={setSelectedDatevNumberId}>
                        {editCompany?.datevNumber &&
                          editCompany?.datevNumberId !== locationDatevNumber?.datevNumber?._id && (
                            <SelectInput.Option value={editCompany?.datevNumber?._id}>
                              {editCompany?.datevNumber?.datevNumber}
                            </SelectInput.Option>
                          )}
                        {locationDatevNumber?.datevNumber?._id && (
                          <SelectInput.Option value={locationDatevNumber?.datevNumber?._id}>
                            {locationDatevNumber?.datevNumber?.datevNumber}
                          </SelectInput.Option>
                        )}

                        {datevNumbers.map((datevNumber: any) => (
                          <SelectInput.Option
                            value={datevNumber._id}
                            key={datevNumber._id}
                            disabled={
                              datevNumber._id === selectedDatevNumberId &&
                              datevNumber._id !== companyDatevNumberId
                            }
                          >
                            {datevNumber.datevNumber}
                          </SelectInput.Option>
                        ))}
                      </SelectInput>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            )}
            <Form.Item
              name={[field.name, 'companyNumber']}
              label={t('settings:company.labels.companyNumber')}
              rules={[
                {
                  pattern: /^\d{8}$/,
                  message: t('settings:companySettings.validation.mustBeExactDigits', {
                    value: '8',
                  }),
                },
              ]}
            >
              <NumberInput
                allowNegative={false}
                thousandSeparator={false}
                allowLeadingZeros={false}
                placeholder="27390000"
                maxLength={8}
              />
            </Form.Item>
          </FormItemWrapper>
          <BusinessAddressFormList parentField={field.name} form={form} />
          <Separator />
        </LocationWrapper>
      ))}
    </div>
  );
};

export default LocationFormList;
